export type EventListener<Payload> = (payload: Payload) => void;

export type EmitArgs<Payload> = Payload extends void ? [] : [payload: Payload];

export class TypedEventEmitter<Events extends Record<string, unknown>> {
  private readonly listeners = new Map<keyof Events, Set<EventListener<never>>>();

  on<K extends keyof Events>(event: K, listener: EventListener<Events[K]>): () => void {
    const eventListeners = this.listeners.get(event) ?? new Set<EventListener<never>>();
    eventListeners.add(listener as EventListener<never>);
    this.listeners.set(event, eventListeners);

    return () => this.off(event, listener);
  }

  once<K extends keyof Events>(event: K, listener: EventListener<Events[K]>): () => void {
    const onceListener: EventListener<Events[K]> = (payload) => {
      this.off(event, onceListener);
      listener(payload);
    };

    return this.on(event, onceListener);
  }

  off<K extends keyof Events>(event: K, listener: EventListener<Events[K]>): void {
    this.listeners.get(event)?.delete(listener as EventListener<never>);
  }

  emit<K extends keyof Events>(event: K, ...args: EmitArgs<Events[K]>): void {
    const eventListeners = this.listeners.get(event);
    if (!eventListeners?.size) return;

    const [payload] = args as unknown as [Events[K]];
    let firstError: unknown = null;
    let hasError = false;
    Array.from(eventListeners).forEach((listener) => {
      try {
        (listener as EventListener<Events[K]>)(payload);
      } catch (error) {
        if (!hasError) {
          hasError = true;
          firstError = error;
        }
      }
    });
    if (hasError) throw firstError;
  }

  removeAllListeners(event?: keyof Events): void {
    if (event === undefined) {
      this.listeners.clear();
      return;
    }
    this.listeners.delete(event);
  }
}
